import { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { baseURL } from '../config'
import toast from 'react-hot-toast';
import AuthContext from './AuthContext';
import PopupWithClose from '../Components/PopupWithClose';
import Popup from '../Components/Popup';
import ButtonSolid from '../Components/ButtonSolid';

const FileSyncContext = createContext();

const FileSyncProvider = ({ children }) => {
  const { token } = useContext(AuthContext);
  const [checkLocalStorage, setCheckLocalStorage] = useState(true);
  const [failedData, setFailedData] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);

  // Sync Audit States
  const [isSyncAuditRunning, setIsSyncAuditRunning] = useState(false);
  const [syncAuditReport, setSyncAuditReport] = useState(null);
  const [showSyncAuditPopup, setShowSyncAuditPopup] = useState(false);

  const setClosePopup = () => {
    setFailedData([])
  }
  const checkSyncControlPackageStatus = async () => {
    try {
      const response = await axios.get(`${baseURL}/asset/checkCPMergeStatus`, {
        headers: {
          "ngrok-skip-browser-warning": "any",
          "x-access-token": token,
        },
      });
      const status = response.data.data.status.status;

      if (status === "completed" || status === "stopped") {
        sessionStorage.setItem('syncCPLoading', false);
        setInitialLoad(false); // to prevent toast on first load even if already completed
        if (!initialLoad && status === "completed") {
          toast.success("Sucessfully Synced Control Packages");
        }
        if (!initialLoad) {
          setFailedData(response.data.data.status.failed_data)
        }
        setCheckLocalStorage(!checkLocalStorage)
      } else if (status === "failed") {
        sessionStorage.setItem('syncCPLoading', false);
        if (!initialLoad) {
          toast.error("Sync Control Packages failed. Please try again.");
        }
        setInitialLoad(false);
        setCheckLocalStorage(!checkLocalStorage)
      } else if (status === "running") {
        sessionStorage.setItem('syncCPLoading', true);
        setTimeout(checkSyncControlPackageStatus, 60000); // Retry after 1min
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error checking Sync Control Packages status.");
      setCheckLocalStorage(!checkLocalStorage)
    }
  };

  const checkSyncAssetsStatus = async () => {
    try {
      const response = await axios.get(`${baseURL}/asset/checkAssetMergeStatus`, {
        headers: {
          "ngrok-skip-browser-warning": "any",
          "x-access-token": token,
        },
      });
      const status = response.data.data.status.status;
      if (status === "completed" || status === "stopped") {
        sessionStorage.setItem('syncAssetsLoading', false);
        setInitialLoad(false); // to prevent toast on first load even if already completed
        if (!initialLoad && status === "completed") {
          toast.success("Sucessfully Synced Assets");
        }
        setCheckLocalStorage(!checkLocalStorage)
      } else if (status === "failed") {
        sessionStorage.setItem('syncAssetsLoading', false);
        if (!initialLoad) {
          toast.error("Sync Assets failed. Please try again.");
        }
        setInitialLoad(false);
        setCheckLocalStorage(!checkLocalStorage)
      } else if (status === "running") {
        sessionStorage.setItem('syncAssetsLoading', true);
        setTimeout(checkSyncAssetsStatus, 30000); // Retry after 30sec
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error checking Sync Assets status.");
      sessionStorage.setItem('syncAssetsLoading', false);
      setCheckLocalStorage(!checkLocalStorage)
    }
  };

  const syncAudit = async () => {
    try {
      setIsSyncAuditRunning(true);
      const response = await axios.get(`${baseURL}/asset/dryRunSync`, {
        headers: {
          "x-access-token": token,
          "ngrok-skip-browser-warning": "any"
        },
      });

      if (response.status === 200) {
        setSyncAuditReport(response.data.data);
        setShowSyncAuditPopup(true);
        toast.success("Sync Audit report generated");
      }
    } catch (error) {
      console.error("Error in Sync Audit:", error);
      toast.error("Failed to generate Sync Audit report");
    } finally {
      setIsSyncAuditRunning(false);
    }
  };

  useEffect(() => {
    if (token) {
      checkSyncAssetsStatus();
      checkSyncControlPackageStatus();
    }
    // eslint-disable-next-line
  }, [token]);

  return (
    <FileSyncContext.Provider value={{
      checkSyncControlPackageStatus,
      checkLocalStorage,
      checkSyncAssetsStatus,
      syncAudit,
      isSyncAuditRunning
    }}>

      {failedData?.length > 0 &&
        <PopupWithClose closePopup={setClosePopup}>
          <p className='text-xl font-semibold'>Data failed to sync into control {failedData?.length > 1 ? 'packages' : 'package'}</p>
          <p className='text-sm text-gray-600 font-sm mb-6' >{`( Failed to sync due to missing asset files for electric/pneumatic schematics. )`}</p>
          <ul className='flex flex-wrap text-nowrap gap-4'>
            {failedData.map((model_no, index) => (
              <li key={index}>{model_no}</li>
            ))}
          </ul>
        </PopupWithClose>
      }

      {showSyncAuditPopup && syncAuditReport && (
        <Popup>
          <div className="max-h-[80vh] overflow-y-auto">
            <h3 className='text-lg font-semibold mb-4 border-b pb-2'>Sync Audit Summary</h3>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
              <div className='bg-slate-50 p-4 rounded-lg border'>
                <h4 className='font-medium text-blue-700 mb-2'>Asset Sync Status</h4>
                <p className='text-sm mb-1'><b>Analyzed Files:</b> {syncAuditReport.sync_assets.counts.processed_files}</p>
                <div className='mt-2 space-y-1 text-sm'>
                  <p><b>Manuals:</b> {syncAuditReport.sync_assets.manuals.new.length} New, {syncAuditReport.sync_assets.manuals.updated.length} Updated, {syncAuditReport.sync_assets.manuals.obsolete.length} Obsolete</p>
                  <p><b>Videos:</b> {syncAuditReport.sync_assets.videos.new.length} New, {syncAuditReport.sync_assets.videos.updated.length} Updated, {syncAuditReport.sync_assets.videos.obsolete.length} Obsolete</p>
                  <p className='text-red-600'><b>Missing Assemblies:</b> {syncAuditReport.sync_assets.assemblies.non_existing_assembly.length}</p>
                </div>
              </div>

              <div className='bg-slate-50 p-4 rounded-lg border'>
                <h4 className='font-medium text-blue-700 mb-2'>Control Packages Status</h4>
                <p className='text-sm mb-1'><b>Total Assemblies:</b> {syncAuditReport.control_packages.counts.total_assemblies}</p>
                <div className='mt-2 space-y-1 text-sm'>
                  <p className='text-green-600'><b>Ready to Merge:</b> {syncAuditReport.control_packages.ready_to_merge.length}</p>
                  <p className='text-orange-600'><b>Missing Assets:</b> {syncAuditReport.control_packages.missing_assets.length}</p>
                </div>
              </div>
            </div>

            {syncAuditReport.sync_assets.assemblies.non_existing_assembly.length > 0 && (
              <div className='mb-6'>
                <h4 className='font-medium text-sm text-red-700 mb-2'>Missing Model/Assembly Numbers in DB:</h4>
                <div className='bg-red-50 p-3 rounded border border-red-100 max-h-32 overflow-y-auto text-xs'>
                  {syncAuditReport.sync_assets.assemblies.non_existing_assembly.join(', ')}
                </div>
              </div>
            )}

            <div className='flex justify-end'>
              <ButtonSolid
                buttonText="Close Report"
                onClick={() => setShowSyncAuditPopup(false)}
              />
            </div>
          </div>
        </Popup>
      )}

      {children}
    </FileSyncContext.Provider>
  );
};

export { FileSyncProvider };

export default FileSyncContext